import {SubmissionScanImageType} from '../../dashboard/symbols/general.symbols';
import {SubmissionKeyLayerScanVersion} from '../../dashboard/symbols/submission.symbols';
import {HexColor} from '../../shared/symbols';

/**
 * Describes a secondary level brain part for the dashboard.
 * They can be presented only in the specific view if the related main level brain part is selected.
 */
export interface DashboardBrainPartSecondaryLevel {
  /** Unique identifier of the brain part. */
  id: number;

  /** The name of the brain part used to display it in the UI. */
  name: string;

  /** The list of related surface region data. */
  relatedSurfaceRegionData: DashboardBrainPartMriLabel[];

  /** The list of child regions. */
  childRegions: DashboardBrainPartSecondaryLevel[];

  /**
   * Indicates whether the brain part is unavailable for the user.
   * This usually happens when the user has no data for the brain part.
   * The common reason is the lack of the necessary scan type.
   * For example, "Veins" brain part is unavailable if the user has no SWI scan.
   */
  isUnavailable?: boolean;

  /** The reason why the brain part is unavailable. */
  unavailableReason?: string;

  /**
   * The view value for the table in cases when the brain should have a different name in the table view.
   */
  viewValueForTable?: string;

  /**
   * Indicates whether the parent chart should be displayed.
   * Used for cases when the brain part has no related chart data, but parent's chart data should be displayed.
   * Has a lower priority than `shouldHideRelatedChart`.
   */
  shouldDisplayParentChart?: boolean;

  /**
   * Indicates whether the chart should be hidden.
   * Used for cases when the brain part has no related chart data.
   * Has a higher priority than `shouldDisplayParentChart`.
   */
  shouldHideRelatedChart?: boolean;

  /**
   * Not yet implemented on the backend.
   *
   * Indicated the loading priority of the brain part.
   * Where the 1 is the highest priority and 3 is the lowest.
   */
  loadingPriority: number;

  /**
   * Describes the scan type that needed to be approved to display the current brain part region.
   */
  requiredScanType: SubmissionScanImageType;

  /**
   * The list of related scan versions.
   * If the current submission matches the version, the brain part should be presented.
   */
  compatibleKeyLayerVersions: SubmissionKeyLayerScanVersion[];

  /**
   * By default, the statistic is taken from the MRI label when defining the colors for the surface and MRI label.
   * If the value is true, the statistic is taken for the brain section name instead of the MRI label name.
   */
  useBrainPartStatistic: boolean;

  /**
   * The short description of the brain part.
   */
  shortAbout: string;

  /**
   * The detailed description of the brain part.
   * Extends the information from the short about (but it doesn't duplicate it).
   */
  detailedAbout: string;

  /**
   * Indicates whether the root part about should be used.
   */
  useRootPartAbout: boolean;
}

/**
 * Describes a brain part for the dashboard.
 * Each brain part has a name, a list of related label ids and a list of child regions.
 * The related label ids are the ids of the labels that used to display the brain part (related surfaces).
 * The child regions are the regions that are contained in the brain part.
 */
export interface DashboardBrainPart extends DashboardBrainPartSecondaryLevel {
  /** Defines which view type should be used for the brain part. */
  viewType: DashboardBrainPartViewType;

  /** Defines which chart type should be used for the brain part. */
  chartType: DashboardBrainPartChartType;
}

/**
 * Describes a view type for the dashboard brain part.
 */
export enum DashboardBrainPartViewType {
  /** No additional information is displayed, so only the chart and brain region description are displayed. */
  Short = 'short',

  /** Additionally, the clickable list of child regions is displayed in the tree view. */
  Tree = 'tree',

  /** Shows the typical region data, but only for the current region. */
  SinglePercentage = 'single_percentage',

  /** Specific "brain age meter" and the legend for it should be displayed. */
  BrainAge = 'brain_age',

  /**
   * Additionally, the clickable table of related regions is displayed.
   * The table contains the following columns: "brain region", "volume (cm3)", "PVS score".
   * Perivascular Spaces (PVS) related data view type.
   */
  PVS = 'pvs',

  /**
   * Additionally, the clickable table of related regions is displayed.
   * The table contains the following columns: "brain region", "volume (cm3)", "% of region", "scoring".
   * White Matter Hyperintensities (WMH) related data view type.
   */
  WM = 'wm',
}

/**
 * Describes possible chart types for the dashboard brain part.
 */
export enum DashboardBrainPartChartType {
  /**
   * Presents user data point in comparison with the typical range zone.
   *
   * 5-95% represents the typical range zone in relation to other users.
   * 0-4% are considered as the red zone - atypical/low.
   * 96-100% are considered as a good value - atypical/high.
   */
  Typical = 'typical',

  /**
   * Presents user data point in comparison with the typical range zone.
   * Used for ventricles, as the bigger the ventricles are, the worse it is.
   *
   * 5-95% represents the typical range zone in relation to other users.
   * 0-4% are considered as a good value - atypical/high.
   * 96-100% are considered as the red zone - atypical/low.
   */
  TypicalInverted = 'typical_inverted',

  /**
   * Contains data about user's brain age.
   * Each point represents the user's relatively calculated brain age.
   * The chart has no comparison with data of other users.
   */
  BrainAge = 'brain_age',

  /**
   * Contains data about user's Perivascular Spaces (PVS).
   * It has no comparison with other user data.
   *
   * Values 0-20 are considered as normal.
   * Values 21-40 are considered as abnormal.
   *
   * The chart is separated into four zones by its value:
   * 1: 0-10
   * 2: 11-20
   * 3: 21-30
   * 4: 31+
   * Where the first zone is the best and the last one is the worst.
   * Position in the zone defines the PVS score.
   */
  PVS = 'pvs',

  /**
   * Contains data about user's White Matter Hyperintensities (WM).
   * The chart displaying the user's WM volume change over time.
   * It has no comparison, only the dynamic.
   */
  WM = 'wm',
}

/**
 * Describes the parent map for the dashboard brain parts.
 * The result is a relation between the brain part id and its parent id.
 */
export type DashboardBrainPartsParentMap = Map<DashboardBrainPartSecondaryLevel['id'], DashboardBrainPartSecondaryLevel['id']>;

/**
 * Describes the flat list map for the dashboard brain parts.
 * The result is a relation between the brain part id and the brain part.
 */
export type DashboardBrainPartsFlatListMap = Map<DashboardBrainPartSecondaryLevel['id'], DashboardBrainPartSecondaryLevel>;

/**
 * Describes the brain part region backend model.
 */
export interface DashboardBackendBrainPart {
  /** Unique identifier of the brain part. */
  id: number;

  /** The name of the brain part used to display it in the UI. */
  name: string;

  /** Indicates whether the chart should be hidden. */
  hide_related_chart: boolean;

  /** Indicates whether the parent chart should be displayed. */
  display_parent_chart: boolean;

  /**
   * By default, the statistic is taken from the MRI label when defining the colors for the surface and MRI label.
   * If the value is true, the statistic is taken for the brain section name instead of the MRI label name.
   */
  get_statistic_from_brain_section: boolean;

  /** The view type for the brain part. */
  view_type: DashboardBrainPartViewType;

  /** The chart type for the brain part. */
  chart_type: DashboardBrainPartChartType;

  /** The view value for the table in cases when the brain should have a different name in the table view. */
  view_value_for_table: string;

  /** The list of related surface regions ids (it is an identifier of the region in the surface file). */
  subregions: DashboardBackendBrainPart[];

  /** Describes the scan type that needed to be approved to display the current brain part region. */
  scan_source: SubmissionScanImageType;

  /**
   * Defines the loading priority of the brain part.
   * One is the highest priority and three is the lowest.
   */
  loading_priority: 1 | 2 | 3;

  /** The colors of the related brain part surfaces. */
  mri_labels_to_tree_brain_section: DashboardBackendMriLabel[];

  /**
   * The list of related scan versions.
   * If the current submission matches the version, the brain part is presented.
   */
  versions: SubmissionKeyLayerScanVersion[];

  /**
   * The short description of the brain part.
   */
  short_about: string;

  /**
   * The detailed description of the brain part.
   * Extends the information from the short about (but it doesn't duplicate it).
   */
  detailed_about: string;

  /**
   * Indicates whether the root part about should be used.
   */
  use_root_part_about: boolean;
}

/**
 * Describes the brain part MRI label backend model.
 */
export interface DashboardBrainPartMriLabel {
  /** Name of the brain part surface. */
  name: string;

  /** Related surface label id region; used for selection the specific surface region in the 3D viewer. */
  surfaceLabel: number;

  /** Current type of applying the color. */
  colorType: DashboardMriLabelColorType;

  /** The color of the brain part surfaces in case of the typical range (or no range at all). */
  colorTypical: HexColor;

  /** The color of the brain part surfaces in case of the atypical range. */
  colorAtypical: HexColor;

  /** Contains the condition for defining the atypical range. */
  atypicalCondition: DashboardBrainPartMriLabelAtypicalCondition;

  /** The list of related scan labels. */
  scanLabels: number[];

  /** The related brain part id. */
  relatedBrainPart: DashboardBrainPart['id'];
}

/**
 * Describes the brain part MRI label backend model.
 */
export interface DashboardBackendMriLabel {
  /** MRI Label details (from the related model). */
  mri_label: DashboardBackendMriLabelDetails;

  /** The list of related scan labels. */
  tree_brain_section: DashboardBrainPart['id'];

  /** Current type of applying the color. */
  color_type: DashboardMriLabelColorType;

  /** The color of the brain part surfaces in case of the typical range (or no range at all). */
  color_typical: HexColor;

  /** The color of the brain part surfaces in case of the atypical range. */
  color_atypical: HexColor;
}

/**
 * Additional details for the MRI label.
 */
export interface DashboardBackendMriLabelDetails {
  /** Related surface label id region; used for selection the specific surface region in the 3D viewer. */
  surface_label: number;

  /** The list of related scan labels. */
  scan_labels: number[];

  /** Contains the condition for defining the atypical range. */
  atypical_condition: DashboardBrainPartMriLabelAtypicalCondition;

  /** Name of the brain part surface. */
  name: string;
}

/**
 * Describes a possible color-defining type for the MRI Label.
 */
export enum DashboardMriLabelColorType {
  /** The color is defined by the typical and atypical colors (preset percentage conditions). */
  TypicalAtypical = 'typical/atypical',

  /** Based on frontend hardcoded score table (for WHM). */
  ScoringWmh = 'scoring_wm',

  /** Based on frontend hardcoded score table (for PVS). */
  ScoringPvs = 'scoring_pvs',

  /** Only a single color is used for the MRI label (from the typical field). */
  Constant = 'constant',
}

/** Describes the atypical condition for the MRI label. */
export enum DashboardBrainPartMriLabelAtypicalCondition {
  /** Common condition - for the most brain parts, if lower than expected. */
  CommonCondition = '<5',

  /** Inverted condition - for the ventricles, if higher than expected. */
  InvertedCondition = '>95',
}
